import { MigrateEffects } from "./migrate-stakeholders/store/effects/migrate.effects";
import { IntegrationService } from "./core/services/integration.service";
// =====Modules=======
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import { EnvironmentModule } from "./environment/environment.module";
import { RouterModule } from "@angular/router";
import { TraceManagementModule } from "./trace-management/trace-management.module";
import { StoreModule } from "@ngrx/store";
import { EventLogModule } from "./event-log/event.log.module";
import { DeploymentModule } from "./deployment/deployment.module";
import { MaintenanceModule } from "./maintenance/maintenance.module";
import { BzAlertModule } from "./shared/components/alert/alert.module";
import { SyncDataModule } from "./sync-data/sync-data.module";
import { ChangeAdministratorModule } from "./change-administrator/change-administrator.module";
import { CanDeactivateGuard } from "./shared/components/can-deactivate/can-deactivate.guard";

// =====Components=====
import { AppComponent } from "./app.component";
import { SlideNavigationComponent } from "./slide-navigation/slide-navigation.component";
import { EmptyComponent } from "./shared/components/empty/empty.component";
import { DynamicComponent } from "./dynamic/dynamic.component";
import { BpmConsumeComponent } from "./bpm-consume/bpm-consume.component";

// =====Services=====
import { AppDataService } from "./app.data.service";
import { BzAlertService } from "./shared/components/alert/alert.service";
import { CryptoDataService } from "./core/services/crypto.data.service";
import { SyncronizationService } from "./trace-management/core/services/synchronization.service";
import { SlideNavigationService } from "./slide-navigation/slide-navigation.service";
import { NavigationService } from "./core/services/navigation.service";
import { RestApi } from "./auth/rest.api";
import { OAuthClient, AccountsOAuthClient } from "./auth/oauth.client";
import { WSFederationClient } from "./auth/wsfederation.client";
import { AuthClient } from "./auth/auth.client";

// =====Reducers=======
import { routing } from "./app.routes";
import { InformationBarActions } from "./core/reducers/informationBar.actions";
import { ReducersService } from "./core/services/reducers.service";
import { TraceActions } from "./trace-management/core/reducers/trace.actions";
import { ViewerActions } from "./trace-management/core/reducers/viewer.actions";
import { EnvironmentActions } from "./core/reducers/environment.actions";
import { BzAlertActions } from "./shared/components/alert/core/reducers/alert.actions";
import { SharedModule } from "./shared/shared.module";
import { CanActivateViaAuthGuard } from "./bz-authentication/bz-can-activate-auth";
import { ExceptionsModule } from "./exceptions/exceptions.module";
import { ButtonHamburguerComponent } from "./button-hamburguer/button-hamburguer.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { TranslateLoader, TranslateModule, MissingTranslationHandler } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { ROOT_REDUCER, INITIAL_STATE } from "./store";
import { RestApiInterceptorService } from "./auth/rest-api-interceptor.service";
import { SecurityModule } from "./security/security.module";
import { BzgSidebarModule } from "bz-genome-angular";
import { SlideSettingsNavigationComponent } from "./slide-navigation/settings/slide-settings-navigation.component";
import { SlideMainNavigationComponent } from "./slide-navigation/main/slide-main-navigation.component";
import { AutoLoginClient } from "./auth/auto-login.client";
import { ChangePasswordComponent } from "./auth/auto-login/change-password/change-password.component";
import { ManagementConsoleComponent } from "./management-console/management-console.component";
import { NotFoundComponent } from "./auth/not-found/not-found.component";
import { AdminLoginComponent } from "./auth/auto-login/admin-login/admin-login.component";
import { EmailSentComponent } from "./auth/auto-login/email-sent/email-sent.component";
import { FormsModule } from "@angular/forms";
import { ProfileActions } from "./core/reducers/profile.actions";
import { AccountInformationModule } from "./account-information/account-information.module";
import { AppMissingTranslationHandler } from "./app-missing-translation.handler";
import { SendAdminEmailComponent } from "./auth/auto-login/send-admin-email/send-admin-email.component";
import { SendPasswordEmailComponent } from "./auth/auto-login/send-password-email/send-password-email.component";
import { CanActivateAutologinGuard } from "./bz-authentication/bz-can-activate-autologin";
import { CanActivateInSessionGuard } from "./bz-authentication/bz-can-activate-in-session";
import { ConnectModule } from "./connect/connect.module";
import { MenuOptionsActions } from "./core/reducers/options.actions";
import { ConfigActions } from "./core/reducers/config.actions";
import { SchedulerModule } from "./scheduler/scheduler.module";
import { ChangePasswordAdminModule } from "./change-password-admin/change-password-admin.module";
import { OrganizationModule } from "./organization/organization.module";
import { DeleteCasesModule } from "./delete-cases/delete-cases.module";
import { RpaBotsModule } from "./rpa-bots/rpa-bots.module";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { EffectsModule } from "@ngrx/effects";
import { FilterUsersEffects } from "./organization/core/store/effects/filter-users.effects";
import { UserGroupsListEffects } from "./organization/user-groups/store/effects/user-groups-list.effects";
import { CurrentUserGroupEffects } from "./organization/user-groups/store/effects/current-user-group.effects";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { environment } from "environments/environment";
import { OutOfServiceComponent } from "./auth/out-of-service/out-of-service.component";
import { ExcelConnectorListEffects } from "app/connect/connect-excel/store/effects/excel-connector-list.effects";
import { ExcelConnectorCurrentEffects } from "./connect/connect-excel/store/effects/excel-connector-current.effects";
import { UIPathConnEffects } from "./rpa-bots/uipath/store/effects/rpa-uipath-conn.effects";
import { BluePrismConnEffects } from "./rpa-bots/blue-prism/store/effects/rpa-blueprism-conn.effects";
import { AutomationAnywhereConnEffects } from "./rpa-bots/automation-anywhere/store/effects/rpa-automation-anywhere-conn.effects";
import { AuthenticationEffects } from "app/security/security-authentication/store/effects/authentication.effects";
import { Accounts2ClientService } from "./auth/accounts2.client";
import { UIPathDashboardEffects } from "./rpa-bots/uipath/store/effects/rpa-uipath-dashboard.effects";
import { UploadBexFileEffects } from "./deployment/store/effects/upload-bex.effects";
import { PackageVisualizerEffects } from "./deployment/store/effects/package-visualizer.effects";
import { CanActivateEmptyWorkspaceGuard } from "./bz-authentication/bz-can-activate-empty-workspace";
import { ExpirationSessionModalComponent } from "./auth/expiration-session-modal/expiration-session-modal.component";
import { MigrateStakeholdersComponent } from "./migrate-stakeholders/migrate-stakeholders.component";
import { ProgressBarModule } from "angular-progress-bar";
import { GlobalProxyModule } from "./global-proxy/global-proxy.module";
import { DataVisualizationModule } from "./datavisualization/datavisualization.module";
import { I18nModule } from "i18n/i18n.module";
import { SequencesModule } from "./sequences/sequences.module";
import { ModalModule } from "ngx-bootstrap/modal";
import { BsDatepickerModule } from "ngx-bootstrap/datepicker";
import { saveAs } from 'file-saver';
import { SyncFilesModule } from "./sync-files/sync-files.module";
import { InitialPageComponent } from './initial-page/initial-page.component';
import { TimezoneModule } from "./timezone/timezone.module";
import { BaseKnowledgeCatalogModule } from "./base-knowledge-catalog/base-knowledge-catalog.module";
import { AIForProcessesModule } from "./ai-for-processes/ai-for-processes.module";
import { AIJobModule } from "./ai-job/ai-job.module";
import { EncryptionModule } from "./encryption/encryption.module";

export function saveFile(content: Blob, fileName: string) {
  saveAs(content, fileName);
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "./i18n/", ".json");
}

const effects = [
  UserGroupsListEffects,
  FilterUsersEffects,
  CurrentUserGroupEffects,
  ExcelConnectorListEffects,
  ExcelConnectorCurrentEffects,
  UIPathConnEffects,
  UIPathDashboardEffects,
  BluePrismConnEffects,
  AutomationAnywhereConnEffects,
  AuthenticationEffects,
  UploadBexFileEffects,
  PackageVisualizerEffects,
  MigrateEffects
];

@NgModule({
  declarations: [
    AppComponent,
    SlideNavigationComponent,
    SlideMainNavigationComponent,
    SlideSettingsNavigationComponent,
    DynamicComponent,
    EmptyComponent,
    ButtonHamburguerComponent,
    BpmConsumeComponent,
    ChangePasswordComponent,
    AdminLoginComponent,
    EmailSentComponent,
    SendPasswordEmailComponent,
    SendAdminEmailComponent,
    NotFoundComponent,
    ManagementConsoleComponent,
    OutOfServiceComponent,
    ExpirationSessionModalComponent,
    MigrateStakeholdersComponent,
    InitialPageComponent
  ],
  imports: [
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: AppMissingTranslationHandler
      }
    }),
    BrowserModule,
    ExceptionsModule,
    ModalModule.forRoot(),
    BsDatepickerModule.forRoot(),
    SharedModule,
    I18nModule,
    HttpClientModule,
    EnvironmentModule,
    SyncDataModule,
    SyncFilesModule,
    TraceManagementModule,
    EventLogModule,
    DeploymentModule,
    MaintenanceModule,
    OrganizationModule,
    RpaBotsModule,
    SchedulerModule,
    BzAlertModule,
    StoreModule.forRoot(ROOT_REDUCER, {
      initialState: INITIAL_STATE
    }),
    EffectsModule.forRoot(effects),
    StoreRouterConnectingModule.forRoot({ stateKey: "router" }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production
    }),
    routing,
    BrowserAnimationsModule,
    SecurityModule,
    DataVisualizationModule,
    BzgSidebarModule,
    FormsModule,
    ChangeAdministratorModule,
    AccountInformationModule,
    ConnectModule,
    DeleteCasesModule,
    ChangePasswordAdminModule,
    ProgressBarModule,
    GlobalProxyModule,
    SequencesModule,
    TimezoneModule,
    BaseKnowledgeCatalogModule,
    EncryptionModule,
    AIForProcessesModule,
    AIJobModule
  ],
  providers: [
    ConfigActions,
    InformationBarActions,
    TraceActions,
    ViewerActions,
    EnvironmentActions,
    MenuOptionsActions,
    ReducersService,
    AppDataService,
    BzAlertService,
    BzAlertActions,
    CryptoDataService,
    IntegrationService,
    SyncronizationService,
    CanActivateViaAuthGuard,
    CanActivateAutologinGuard,
    CanActivateInSessionGuard,
    CanActivateEmptyWorkspaceGuard,
    CanDeactivateGuard,
    SlideNavigationService,
    NavigationService,
    { provide: OAuthClient, useClass: AccountsOAuthClient },
    WSFederationClient,
    RestApi,
    AuthClient,
    AutoLoginClient,
    ProfileActions,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RestApiInterceptorService,
      multi: true
    },
    Accounts2ClientService
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}
